import { Breadcrumb, EntityContext, EntityContextType, NotFound, PermissionDenied, useEntity } from 'components';
import { PresenceUsersProvider } from 'components/PresenceUsers';
import {
  Dispatch,
  MutableRefObject,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useChannelSubscription, ChannelProvider, useChannelListener, Fetcher, api } from 'services';

import invariant from 'tiny-invariant';
import { CampaignDetails } from '../campaign/types';
import { BlockHeader } from './BlockHeader';
import { BlockDetails } from './types';
import { toast } from 'react-toastify';
import { LoadingContainer } from '@ff-it/ui';
import { BlockChannelEvent } from './events';
import { ChannelEvent } from 'services/realtime/protocol';
import Mutex, { prompStale } from 'utilities';
import { RequestArgs, RequestFailure } from '@ff-it/api';

export type RequestHandler = Fetcher & {
  revisionRef: MutableRefObject<number | undefined>;
};

const RequestHandlerContext = createContext<RequestHandler | undefined>(undefined);

export function useRequestHandler(): RequestHandler {
  const handler = useContext(RequestHandlerContext);
  invariant(handler);
  return handler;
}

export function useSyncFetcher(
  endpoint: string,
): [RequestHandler, isFetching: boolean, setFetching: Dispatch<SetStateAction<boolean>>] {
  const mutexRef = useRef<Mutex | null>(null);
  if (!mutexRef.current) mutexRef.current = new Mutex();

  const revisionRef = useRef<number>();
  const activeRequests = useRef(0);
  const [isFetching, setFetching] = useState<boolean>(false);

  const fetcher = useMemo(() => {
    const fn = async <R,>(args: RequestArgs): Promise<R> => {
      activeRequests.current++;
      setFetching(true);

      const unlock = await mutexRef.current!.lock();

      try {
        const headers = {
          ...args.headers,
          ...(revisionRef.current !== undefined && { 'X-If-Revision': revisionRef.current.toString() }),
        };

        const res = await api.request<R, any>({ ...args, url: `${endpoint}${args.url}`, headers });

        if (!res.ok) {
          if (args.url === 'submit/' && res.status === 409 && res.data.detail === 'Plan is not complete') {
            window.__GRID__ && window.__GRID__.focusInvalid();
          }
          if (res.status === 412) {
            prompStale();
          }

          throw res;
        }

        const xRev = res.response.headers.get('X-Revision');
        if (xRev) {
          revisionRef.current = parseInt(xRev, 10);
        }

        return res.data;
      } finally {
        activeRequests.current--;
        if (activeRequests.current === 0) {
          setFetching(false);
        }
        unlock();
      }
    };

    const fetcherWithRef = fn as RequestHandler;
    fetcherWithRef.revisionRef = revisionRef;

    return fetcherWithRef;
  }, [endpoint]);

  return [fetcher, isFetching, setFetching];
}

function useBlockFetcher(fetcher: RequestHandler) {
  const [data, setData] = useState<BlockDetails | null>(null);
  const [error, setError] = useState<RequestFailure<unknown> | null>(null);

  const versionRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const doFetch = useCallback(
    async (version: number) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);

      try {
        // @TODO abort signal
        const result = await fetcher<BlockDetails>({ url: '', method: 'GET' });

        if (version !== versionRef.current) return;
        if (!mountedRef.current) return;

        setData(result);
      } catch (err) {
        if (version !== versionRef.current) return;
        if (!mountedRef.current) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;

        setError(err as RequestFailure<unknown>);
      }
    },
    [fetcher],
  );

  useEffect(() => {
    mountedRef.current = true;
    const version = ++versionRef.current;
    doFetch(version);

    return () => {
      // Deferred abort: survives StrictMode's synchronous remount.
      // Real unmount → microtask fires → abort.
      // StrictMode → remount sets mountedRef back to true before microtask.
      mountedRef.current = false;
      queueMicrotask(() => {
        if (!mountedRef.current) {
          abortRef.current?.abort();
        }
      });
    };
  }, [doFetch]);

  const mutate = useCallback(
    (
      update?: BlockDetails | ((prev: BlockDetails | null) => BlockDetails | null) | undefined,
      options: any = undefined,
    ) => {
      if (options) {
        throw new Error('Not supported');
      }
      if (update !== undefined) {
        setData(typeof update === 'function' ? (update as (prev: BlockDetails | null) => BlockDetails | null) : update);
      } else {
        // invalidate
        const version = ++versionRef.current;
        doFetch(version);
      }
    },
    [doFetch],
  );

  return { data, setData, error, mutate };
}

function BlockChannel({
  children,
  mutate,
  id,
  requestHandler,
}: {
  children: ReactNode;
  id: number;
  mutate: EntityContextType<BlockDetails>['mutate'];
  requestHandler: RequestHandler;
}): ReactElement {
  const channel = useChannelSubscription(`campaign:block:${id}`);

  useChannelListener(
    channel,
    useCallback(
      (message: ChannelEvent<BlockChannelEvent>) => {
        const expectedRevision = (requestHandler.revisionRef.current || 0) + 1;
        const revision = message.payload.revision;
        console.log(`[BlockChannel]: event=${message.payload.event} revision=${revision}`);
        if (!revision || revision < expectedRevision) {
          console.warn(`Stale or invalid revision `);
          return;
        }
        // we missed some uptaes
        if (revision !== expectedRevision) {
          mutate();
          toast.info('Resyncing concurrent update');
          return;
        }

        switch (message.payload.event) {
          case 'BLOCK/INVALIDATE':
            mutate();
            toast.info('Concurrent update');
            return;
          case 'PLAN/PATCH':
            // Someone updated plan, we handle this downstream if plan is mounted
            requestHandler.revisionRef.current = message.payload.revision;
            return;
          default:
            mutate();
            toast.info('Concurrent update');
            throw new Error(`Unexcpted event: ${message.payload}`);
        }
      },
      [mutate, requestHandler],
    ),
  );

  return (
    <ChannelProvider channel={channel}>
      <PresenceUsersProvider>{children}</PresenceUsersProvider>
    </ChannelProvider>
  );
}

export function BlockProvider(): ReactElement {
  const { item: campaign } = useEntity<CampaignDetails>();

  const { block_id } = useParams<'block_id'>();
  invariant(block_id);
  const endpoint = `campaign/blocks/${block_id}/`;

  /**
   * We want to protect our block and plan actions against the races and concurrent updates
   * We make them serialized by waiting for mutext, and mark them with revision tag
   */
  const [fetcher, isFetching, setFetching] = useSyncFetcher(endpoint);
  // We don't use swr here since we don't want to cache
  const { data, setData, error, mutate } = useBlockFetcher(fetcher);

  if (error) {
    if (error.status === 404) {
      return <NotFound />;
    }
    if (error.status === 403) {
      return <PermissionDenied />;
    }
    throw error;
  }

  let content = null;

  if (data) {
    const ctx: EntityContextType<BlockDetails> = {
      endpoint,
      item: data,
      setSpinning: setFetching,
      // provides sync handler for block actions, status change in header
      setItem: setData as EntityContextType<BlockDetails>['setItem'],
      mutate: mutate as EntityContextType<BlockDetails>['mutate'],
    };

    content = (
      <RequestHandlerContext.Provider value={fetcher}>
        <BlockChannel requestHandler={fetcher} mutate={ctx.mutate} id={ctx.item.id}>
          <EntityContext.Provider value={ctx}>
            <BlockHeader ctx={ctx} endpoint={ctx.endpoint} campaign={campaign} />
            <Breadcrumb>{ctx.item.code}</Breadcrumb>
            <Outlet />
          </EntityContext.Provider>
        </BlockChannel>
      </RequestHandlerContext.Provider>
    );
  }
  return (
    <LoadingContainer loading={isFetching} className="flex-grow-1 flex-column d-flex">
      {content}
    </LoadingContainer>
  );
}
